import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { type ReceiptCard, type ReceiptStatus, listReceipts } from "@/api/transfer_receipts";
import { Button } from "@/components/ui/button";
import { AccessDenied, LoadState, isForbidden } from "@/lib/scan-ui/screen";
import { ReceiptCardView } from "./shared";

/** Порядок групп «Входящих»: что едет — первым, затем разбор и принятое за сутки. */
const GROUPS: { status: ReceiptStatus; title: string }[] = [
  { status: "sent", title: "Едет к вам" },
  { status: "discrepancy", title: "На разборе" },
  { status: "completed", title: "Принято за последние 24 ч" },
];

/**
 * «Приёмка перемещений» — входящие: что к получателю едет, откуда, кто и когда
 * отправил. Видимость решает бэк (привязка пользователя к складам); без
 * привязки — 403 с текстом, и экран показывает ЕГО, а не пустой список:
 * пустые «Входящие» неотличимы от «ничего не едет».
 */
export function TransferReceiptListPage(): React.ReactElement {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["transfer-receipts"], queryFn: listReceipts });

  const rows = q.data?.items ?? [];
  const byStatus = (s: ReceiptStatus): ReceiptCard[] => rows.filter((r) => r.status === s);

  return (
    <div className="mx-auto max-w-xl space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Приёмка перемещений</h1>
          <p className="text-sm text-muted-foreground">Входящие на ваши склады</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label="Обновить"
          onClick={() => void q.refetch()}
          disabled={q.isFetching}
        >
          <RotateCcw className="size-4" />
        </Button>
      </div>

      {q.isError && isForbidden(q.error) ? (
        <AccessDenied error={q.error} className="flex flex-col items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-6 text-center" />
      ) : q.isLoading || q.isError ? (
        <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} className="flex flex-col items-center gap-3 p-6 text-center" />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Ничего не едет: входящих перемещений нет.
        </p>
      ) : (
        GROUPS.map(({ status, title }) => {
          const items = byStatus(status);
          if (items.length === 0) return null;
          return (
            <section key={status} className="space-y-2" aria-label={title}>
              <h2 className="text-sm font-semibold text-muted-foreground">
                {title} ({items.length})
              </h2>
              {items.map((r) => (
                <ReceiptCardView
                  key={r.session_id}
                  row={r}
                  onClick={() => navigate(`/transfer-receipts/${r.session_id}`)}
                />
              ))}
            </section>
          );
        })
      )}
    </div>
  );
}
